/**
 * Builds plain text and HTML strings for copying committee ranking data
 * to the clipboard.
 *
 * @param {Object} params
 * @param {Object} params.committee - The committee object
 * @param {string} params.chairName - The chair's name
 * @param {string} params.observers - The observers string
 * @param {string} params.lastColLabel - The last column label (Country or State/Territory)
 * @param {Function} params.formatCandidate - Candidate formatter function
 * @returns {{ plainText: string, htmlText: string }}
 */
export const buildClipboardHtml = ({
  committee,
  chairName,
  observers,
  lastColLabel,
  formatCandidate,
}) => {
  let plainText = `${committee.sectionalCommitteeName}\n`;
  plainText += `${'='.repeat(committee.sectionalCommitteeName.length)}\n`;
  plainText += `Chair: ${chairName}\n`;
  plainText += `Observers: ${observers}\n\n`;

  if (committee.candidates.length === 0) {
    plainText += `No ranked candidates.\n`;
  } else {
    committee.candidates.forEach((candidate) => {
      const f = formatCandidate(candidate, committee.electionTypeId);
      plainText += `Rank ${f.rank}: ${f.name}\n`;
      plainText += `  Gender: ${f.gender}\n`;
      plainText += `  Country of Birth: ${f.country}\n`;
      plainText += `  Institution: ${f.institution}\n`;
      plainText += `  ${lastColLabel}: ${f.stateOrCountry}\n`;
      plainText += `  Field of Expertise: ${f.expertise}\n`;
      plainText += `\n`;
    });
    plainText += `Total ranked candidates: ${committee.candidates.length}\n`;
  }

  let htmlText = `
    <div style="font-family: Arial, sans-serif;">
      <h2 style="color: #0d6efd; border-bottom: 2px solid #0d6efd; padding-bottom: 5px; margin-bottom: 5px;">
        ${committee.sectionalCommitteeName}
      </h2>
      <p style="margin-bottom: 20px;">
        <strong>Chair:</strong> ${chairName}<br>
        <strong>Observers:</strong> ${observers}
      </p>
  `;

  if (committee.candidates.length === 0) {
    htmlText += `<p><em>No ranked candidates.</em></p>`;
  } else {
    htmlText += `
      <table style="border-collapse: collapse; width: 100%; margin-bottom: 15px;">
        <thead>
          <tr style="background-color: #f8f9fa;">
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Rank</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Name</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Gender</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Country of Birth</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Institution</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">${lastColLabel}</th>
            <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Field of Expertise</th>
          </tr>
        </thead>
        <tbody>
    `;

    committee.candidates.forEach((candidate) => {
      const f = formatCandidate(candidate, committee.electionTypeId);
      htmlText += `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center; font-weight: bold;">${f.rank}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${f.name}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${f.gender}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${f.country}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${f.institution}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${f.stateOrCountry}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${f.expertise}</td>
        </tr>
      `;
    });

    htmlText += `
          </tbody>
        </table>
        <p style="font-weight: bold;">Total ranked candidates: ${committee.candidates.length}</p>
      </div>
    `;
  }

  return { plainText, htmlText };
};
